import React, { useState, useContext } from "react";
import { Card, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AsyncSelect from "react-select/async";
import { LanguageContext } from "App";
import UsersApi from "../../Classes/API/UsersApi";
import SearchLogsReq from "../../Classes/Models/Requests/Accounting/SearchLogsReq";
import searchUsersReq from "../../Classes/Models/Requests/Users/SearchUser";
import QuickDate from "../../components/QuickDate";
import { Margin } from "@mui/icons-material";

const LogsSearch = ({ setSearchReq, setPage }) => {
  const { translations } = useContext(LanguageContext);
  const [id, setId] = useState("");
  const [accSessionId, setAccSessionId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [type, setType] = useState("");
  const isRtl = translations.setlang === "ar";

  const logTypes = [
    { value: "", label: "All Types" },
    { value: "Appointments", label: "Appointments" },
    { value: "Accounting", label: "Accounting" },
    { value: "Sessions", label: "Sessions" },
    { value: "Administration", label: "Administration" },
    { value: "Users", label: "Users" },
    { value: "Packages", label: "Packages" },
    { value: "Contracts", label: "Contracts" },
    { value: "Accounting Sessions", label: "Accounting Sessions" },
    { value: "Accounting - Packages", label: "Accounting - Packages" },
  ];

  const getUsers = async (inputValue = "") => {
    const searchReq = new searchUsersReq();
    searchReq.name = inputValue;
    searchReq.isActivated = true;
    searchReq.isSoftDeleted = false;
    searchReq.pagingParams = { pageNumber: 1, pageSize: 50 };

    const res = await UsersApi.SearchUsers(searchReq);

    if (res.status === "Success") {
      return res.data.list
        .filter((user) => {
          const hasType = user.userType && user.userType.trim() !== "";
          const isNotPatient = user.userType.toLowerCase() !== "patient";
          return hasType && isNotPatient;
        })
        .map((item) => ({
          value: item,
          label: item.name,
        }));
    }
    return [];
  };
  const getPatients = async (inputValue = "") => {
    const searchReq = new searchUsersReq();
    searchReq.name = inputValue;
    searchReq.userType = "Patient";
    const res = await UsersApi.SearchUsers(searchReq);

    if (res.status === "Success") {
      let optionsArr = res.data.list.map((item) => ({
        value: item,
        label: item.name,
      }));
      return optionsArr;
    }
    return [];
  };

  const onSearchClick = () => {
    const req = new SearchLogsReq();
    setPage(1);
    req.id = id;
    req.accSessionId = accSessionId;
    req.userName = selectedStaff ? selectedStaff.name : "";
    req.patientName = selectedPatient ? selectedPatient.name : "";
    req.logType = type;
    if (fromDate) req.fromDate = `${fromDate}T00:00:00`;
    if (toDate) req.toDate = `${toDate}T23:59:59`;

    setSearchReq(req);
  };

  const dateInputStyle = {
    height: "43px",
    padding: "8px 45px",
    borderRadius: "8px",
    border: "1px solid #d2d6da",
    flex: 1,
    fontSize: "14px",
  };

  return (
    <Card sx={{ padding: "1.5rem" }}>
      <MDBox
        pb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderBottom="1px solid #f0f2f5"
      >
        <MDTypography variant="h6" fontWeight="medium">
          {translations.LogsSearch}
        </MDTypography>
        <MDButton
          variant="gradient"
          sx={{
            backgroundColor: "#3560AD",
            "&:hover": { backgroundColor: "#4682B4" },
          }}
          color="info"
          onClick={onSearchClick}
        >
          {translations.createAccountsessionSingleSessionSearchSearch}
        </MDButton>
      </MDBox>

      <div className={isRtl ? "rtl-label" : ""}>
        <div className="container-fluid mt-3">
          <div className="row align-items-end">
            <div className="col-md-3 mt-3">
              <MDInput
                label={translations.LogsSearchLogId}
                value={id}
                onChange={(e) => setId(e.target.value)}
                type="number"
                fullWidth
              />
            </div>
            <div className="col-md-3 mt-3">
              <MDInput
                label={translations.LogSearchAccSessionId}
                value={accSessionId}
                onChange={(e) => setAccSessionId(e.target.value)}
                type="number"
                fullWidth
              />
            </div>
            <div className="col-md-3 mt-3">
              <MDInput
                select
                fullWidth
                label={translations.LogsSearchType}
                value={type}
                onChange={(e) => setType(e.target.value)}
                InputProps={{
                  style: { minHeight: 43 },
                  endAdornment: (
                    <KeyboardArrowDownIcon
                      style={{
                        marginRight: 4,
                        color: "#6b7280",
                        pointerEvents: "none",
                      }}
                    />
                  ),
                }}
              >
                {logTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </MDInput>
            </div>

            <div className="col-md-3 mt-3">
              <AsyncSelect
                placeholder={translations.LogsSearchUsername}
                isClearable
                loadOptions={getUsers}
                onChange={(e) => setSelectedStaff(e ? e.value : null)}
                value={
                  selectedStaff
                    ? { value: selectedStaff, label: selectedStaff.name }
                    : null
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 43,
                    borderRadius: 8,
                    fontSize: 14,
                    borderColor: "#d2d6da",
                    direction: isRtl ? "rtl" : "ltr",
                  }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>
            <div className="col-md-3 mt-3">
              <AsyncSelect
                placeholder={translations.LogsSearchPatientname}
                isClearable
                loadOptions={getPatients}
                onChange={(e) => setSelectedPatient(e ? e.value : null)}
                value={
                  selectedPatient
                    ? { value: selectedPatient, label: selectedPatient.name }
                    : null
                }
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: 43,
                    fontSize: 14,
                    borderRadius: 8,
                    borderColor: "#d2d6da",
                    direction: isRtl ? "rtl" : "ltr",
                  }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            </div>

            <div className="col-md-3 mt-3">
              <QuickDate
                future={false}
                setFrom={setFromDate}
                setTo={setToDate}
                style={{ height: "43px", width: "100%", fontSize: "14px" }}
              />
            </div>
            <div className="row mt-3 align-items-end">
              <div className="col-md-6 mt-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <input
                    type="date"
                    className="search_input"
                    style={dateInputStyle}
                    onChange={(e) => setFromDate(e.target.value)}
                    value={fromDate}
                  />
                  <MDTypography
                    variant="body2"
                    sx={{ alignSelf: "center", whiteSpace: "nowrap" }}
                  >
                    {translations.PatientStayTo}
                  </MDTypography>
                  <input
                    type="date"
                    className="search_input"
                    style={dateInputStyle}
                    onChange={(e) => setToDate(e.target.value)}
                    value={toDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default LogsSearch;
